import { useCallback, useMemo } from 'react';
import { DynamicIcon } from 'src/components';
import {
  RegisterQuickActionsOptions,
  useRegisterQuickActions,
} from 'src/components/QuickActions/use-register-quick-actions';
import { useMembershipActions } from 'src/components/SidePanelTabs/MembershipInformation/use-membership-actions';
import { useSiteActions } from 'src/components/SiteComponents/use-site-actions';

import { Address, Conversation } from 'src/graphql';
import { useStripeSubscriptionDetails } from 'src/graphql/entities/stripe-subscription-details';
import { useSubscriptionPause } from 'src/graphql/entities/subscription-pause';

const GROUP_NAME = 'Site Actions';
const SITE_ACTION_ORDER = 300;

export const useSiteQuickActions = (
  address: Address,
  conversation?: Conversation,
  options?: RegisterQuickActionsOptions,
) => {
  const {
    updateSite,
    copySiteId,
    copySiteUrl,
    copyTOS,
    mergeSite,
    changeSites,
    disassociateSite,
  } = useSiteActions({
    address,
    conversation,
  });

  const { stripeSubscriptionDetails: subscriptionDetails } = useStripeSubscriptionDetails({
    address,
  });

  const { unpauseSubscription } = useSubscriptionPause();

  const handleUnpauseSubscription = useCallback(() => {
    if (address?.addressId && address?.partnerId) {
      void unpauseSubscription(
        address.addressId,
        address.partnerId,
        subscriptionDetails?.plan,
      );
    }
  }, [address?.addressId, address?.partnerId, subscriptionDetails?.plan, unpauseSubscription]);

  const {
    accessPaymentPortal,
    sendMembershipEmail,
    followUp,
    addComplimentaryPeriod,
    addPreSoldMembership,
    customSignupLink,
    copyCalendlyLink,
    copySubscriptionId,
    pauseSubscription,
    unpauseSubscription: unpauseSubscriptionAction,
  } = useMembershipActions({
    address,
    conversationId: conversation?.conversationId,
    subscriptionDetails,
    onUnpauseSubscription: handleUnpauseSubscription,
  });

  useRegisterQuickActions(
    useMemo(
      () =>
        [
          updateSite,
          copySiteId,
          copySiteUrl,
          // conversation sections will have TOS already
          // so no need to duplicate this action here
          ...(conversation ? [] : [copyTOS]),
          mergeSite,
          changeSites,
          disassociateSite,
          accessPaymentPortal,
          sendMembershipEmail,
          followUp,
          addComplimentaryPeriod,
          addPreSoldMembership,
          { ...customSignupLink, label: 'Copy signup link' },
          copyCalendlyLink,
          copySubscriptionId,
          pauseSubscription,
          unpauseSubscriptionAction,
        ]
          .filter((action) => ('isAllowed' in action ? action.isAllowed : true))
          .map((action) => ({
            ...action,
            id: `${action.label}-${GROUP_NAME}`,
            title: action.label,
            icon: action.icon ? <DynamicIcon name={action.icon} /> : null,
            group: GROUP_NAME,
            order: SITE_ACTION_ORDER,
          })),
      [
        updateSite,
        copySiteId,
        copySiteUrl,
        conversation,
        copyTOS,
        mergeSite,
        changeSites,
        disassociateSite,
        accessPaymentPortal,
        sendMembershipEmail,
        followUp,
        addComplimentaryPeriod,
        addPreSoldMembership,
        customSignupLink,
        copyCalendlyLink,
        copySubscriptionId,
        pauseSubscription,
        unpauseSubscriptionAction,
      ],
    ),
    options,
  );
};
